import { maskDocumentsInText } from './errorPerson';

/**
 * Constrói um conjunto ENXUTO de propriedades para um evento de erro espelhado no PostHog,
 * a partir do `extraData`/erro já usados no Rollbar.
 *
 * Regra: nunca enviar corpos de request, dados de cartão ou payload bruto. Da resposta da API vão só
 * as mensagens (`notifications`, `errors.message`, `message`) e da requisição só o método e a rota,
 * com CPF/CNPJ mascarado. Os dados da pessoa ficam em `buildErrorPersonProps` (`errorPerson.ts`).
 */

const MAX_TEXT = 300;
const MAX_NOTIFICATIONS = 5;

function truncate(value: unknown): string | undefined {
  return typeof value === 'string' && value ? maskDocumentsInText(value).slice(0, MAX_TEXT) : undefined;
}

function requestPath(config: { url?: unknown } | undefined): string | undefined {
  return typeof config?.url === 'string' && config.url ? maskDocumentsInText(config.url) : undefined;
}

function responseNotifications(data: any): string | undefined {
  if (!Array.isArray(data?.notifications) || data.notifications.length === 0) return undefined;
  return data.notifications
    .slice(0, MAX_NOTIFICATIONS)
    .map((n: { key?: unknown; message?: unknown }) => [n?.key, n?.message].filter(Boolean).join(': '))
    .filter(Boolean)
    .map((line: string) => truncate(line))
    .join(' | ');
}

function responseMessage(data: any): string | undefined {
  return truncate(data?.errors?.message) ?? truncate(typeof data?.message === 'string' ? data.message : undefined);
}

export function buildErrorEventProps(
  extraData: Record<string, unknown> = {},
  error: unknown = {}
): Record<string, unknown> {
  const e = (error ?? {}) as {
    name?: string;
    message?: unknown;
    isAxiosError?: boolean;
    config?: { method?: unknown; url?: unknown };
    response?: { status?: number; data?: unknown };
    constructor?: { name?: string };
  };
  const x = (extraData ?? {}) as Record<string, unknown>;

  const status = (x.status as number | undefined) ?? e.response?.status;
  const message = typeof e.message === 'string' ? e.message.slice(0, MAX_TEXT) : undefined;
  const data = e.response?.data;

  const props: Record<string, unknown> = {
    level: 'error',
    error_name: e.name,
    error_message: message,
    error_type: e.isAxiosError ? 'axios' : e.constructor?.name || typeof error,
    status,
    context: x.context,
    http_method: typeof e.config?.method === 'string' ? e.config.method.toUpperCase() : undefined,
    http_path: requestPath(e.config),
    response_notifications: responseNotifications(data),
    response_message: responseMessage(data),
    // ids de negócio (não são PII) úteis para correlacionar no PostHog
    plan_id: x.planId ?? x.planParam,
    contract_id: x.contractId,
    quote_hash: x.quoteHash,
    product_id: x.productId
  };

  // remove chaves undefined para não poluir o evento
  Object.keys(props).forEach((k) => props[k] === undefined && delete props[k]);
  return props;
}
